import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "@/state/types";
import { Box, Typography, Grid, useTheme, Drawer, Toolbar, IconButton, useMediaQuery } from "@mui/material";
import { initMap, useMap } from "../../state/map";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from '@mui/icons-material/Menu';
import { Steps } from "./steps";
import { SubmitStep } from "./steps/submit";
import MapCanvas from "./mapCanvas";
import { STATIC_FILE_URL } from "../../constants";
import { useNavigate } from "react-router-dom";
import { fonts } from "../../fonts";
import ThemeToggleSwitch from "../ThemeToggleSwitch";

export const DRAWER_WIDTH = 400;

const MapMaker = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const map = useMap();
  const mapDiv = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (mapDiv.current) {
      dispatch(initMap(mapDiv.current))
    }
  }, [dispatch, mapDiv]);

  return (
    <Box>
      {isMobile && !isOpen && (
        <IconButton sx={{ position: "absolute", top: "8px", right: "8px", zIndex: 5 }} onClick={() => setIsOpen(true)}>
          <MenuIcon fontSize="large"/>
        </IconButton>
      )}
      <Drawer
        variant="persistent"
        anchor="right"
        sx={{ width: `${DRAWER_WIDTH}px` }}
        open={isOpen || !isMobile}
        onClose={isMobile ? () => setIsOpen(false) : undefined}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", bgcolor: theme.palette.background.default, px: "1rem !important" }}>
          <Typography variant="h4" textAlign="center" onClick={() => navigate("/")} style={{ cursor: "pointer", letterSpacing: 0.3 }}>
            mappin.studio
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ThemeToggleSwitch iconOnly />
            {isMobile && (
              <IconButton sx={{ ml: 1 }} onClick={() => setIsOpen(false)}>
                <CloseIcon/>
              </IconButton>
            )}
          </Box>
        </Toolbar>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: 'calc(100vh - 64px)', 
          width: `${DRAWER_WIDTH}px` 
        }}>
          {/* Scrollable steps area */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            <Steps excludeSubmit={!isMobile} />
          </Box>
          {/* Fixed footer for desktop - always visible */}
          {!isMobile && (
            <Box sx={{ 
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default
            }}>
              <SubmitStep />
            </Box>
          )}
        </Box>
      </Drawer>
      <div
        style={{
          padding: "2rem",
          display: "flex",
          justifyContent: "center",
          marginRight: isMobile ? "0" : `${DRAWER_WIDTH}px`,
          height: "calc(100% - 8rem)",
          overflow: "hidden",
        }}
      >
        <MapCanvas map={map} mapDiv={mapDiv}/>
      </div>
      <Grid container justifyContent="center" mb={1} color="secondary">
        <Typography color="primary" variant="body1">
          Any problems, suggestions, or concerns please email {" "}
          <a href={"mailto:help@mappin.pro"} style={{ color: "white" }}>help@mappin.pro</a>.
        </Typography>
        {/* Hack to preload the fonts. It doesn't showup since the color of the font is the same as background color. */}
        <div style={{ fontFamily: fonts.families.display }}>.</div>
        <div style={{ fontFamily: fonts.families.secondary }}>.</div>
      </Grid>
    </Box>
  );
};

export default MapMaker;
